// Week planner data: parallel queries over the four wired sources, mapped to
// planner events for the displayed week.
//
//   tasks      → GET /tasks?dueFrom&dueTo (server-side range) + overdue pinning
//   meetings   → GET /meetings?from&to (server-side range)
//   tg leads   → GET /integrations/telegram/group-candidates (owner/admin only)
//   deadlines  → GET /deals?activity=due, week-filtered on the client
//
// Workspace scoping goes through the existing feature clients and hooks —
// every query stays gated on `useCurrentScope()`.

import { useCallback, useMemo } from 'react';
import dayjs, { type Dayjs } from 'dayjs';

import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';
import { useDeals } from '../../deals/hooks/useDeals.js';
import { useMeetings } from '../../meetings/hooks/useMeetings.js';
import { useTasks } from '../../tasks/hooks/useTasks.js';
import { useTelegramCandidates } from '../../telegram-candidates/hooks/useTelegramCandidates.js';
import {
  dealDeadlineToEvent,
  meetingToEvent,
  startOfWeekMonday,
  taskToEvent,
  tgCandidateToEvent,
  type PlannerEvent,
} from '../lib/plannerEvents.js';

/** The four wired sources, plus the overdue-task query that feeds the pin. */
export type PlannerSourceKey = 'tasks' | 'overdue' | 'meetings' | 'deals' | 'telegram';

/**
 * One failed source, ready to render as a compact inline notice. `retry`
 * refetches ONLY this source — a broken Telegram integration must not force a
 * refetch of tasks and meetings.
 */
export interface PlannerSourceFailure {
  key: PlannerSourceKey;
  /** i18n key for the notice title («Лиды из Telegram не загрузились»). */
  titleKey: string;
  retry: () => Promise<void>;
}

export interface UsePlannerEventsResult {
  events: PlannerEvent[];
  weekStart: Dayjs;
  weekEnd: Dayjs;
  /** CORE sources (tasks + meetings) still loading — nothing to draw yet. */
  isLoading: boolean;
  /** CORE sources failed (tasks AND meetings): the page shows the full-page error. */
  isError: boolean;
  error: Error | null;
  /** Refetch every source (the header refresh button). */
  refetch: () => Promise<void>;
  /**
   * Per-source failures for the inline notices. A single failed core source is
   * partial data, not a dead page: it is reported here too. The array is empty
   * when every source loaded.
   */
  sourceFailures: PlannerSourceFailure[];
  /** Telegram candidates are reviewable by owners/admins only (API contract). */
  canReviewTelegram: boolean;
}

export function usePlannerEvents(anchor: Dayjs): UsePlannerEventsResult {
  const scope = useCurrentScope();
  const canReviewTelegram = scope?.role === 'owner' || scope?.role === 'admin';

  const weekStart = useMemo(() => startOfWeekMonday(anchor), [anchor]);
  const weekEnd = weekStart.add(6, 'day').endOf('day');
  const from = weekStart.toISOString();
  const to = weekEnd.toISOString();

  const tasksQuery = useTasks({ dueFrom: from, dueTo: to });
  // Overdue tasks that fell out of the visible week stay actionable — the
  // planner pins them to today (prototype: overdue card in today's column).
  const overdueTasksQuery = useTasks({ overdue: true });
  const meetingsQuery = useMeetings({ from, to });
  const dealsQuery = useDeals({ activity: 'due' });
  const tgQuery = useTelegramCandidates({ enabled: canReviewTelegram });

  const plannerEvents = useMemo(() => {
    const startKey = weekStart.format('YYYY-MM-DD');
    const endKey = weekEnd.format('YYYY-MM-DD');
    const todayKey = dayjs().format('YYYY-MM-DD');
    const inWeek = (key: string): boolean => key >= startKey && key <= endKey;
    const todayInWeek = inWeek(todayKey);

    const byId = new Map<string, PlannerEvent>();
    const push = (event: PlannerEvent | null): void => {
      if (event) byId.set(event.id, event);
    };

    for (const task of tasksQuery.data ?? []) {
      push(taskToEvent(task));
    }
    for (const task of overdueTasksQuery.data ?? []) {
      const event = taskToEvent(task);
      if (!event || !event.overdue) continue;
      // In-week overdue tasks already come from the range query; only the
      // ones that fell before the week get pinned to today.
      if (!inWeek(event.dateKey) && todayInWeek) {
        push({ ...event, dateKey: todayKey });
      }
    }
    for (const meeting of meetingsQuery.data ?? []) {
      push(meetingToEvent(meeting));
    }
    if (canReviewTelegram) {
      for (const candidate of tgQuery.data?.items ?? []) {
        push(tgCandidateToEvent(candidate));
      }
    }
    for (const deal of dealsQuery.deals) {
      const event = dealDeadlineToEvent(deal);
      if (event && inWeek(event.dateKey)) push(event);
    }

    return [...byId.values()].filter((event) => inWeek(event.dateKey));
  }, [
    tasksQuery.data,
    overdueTasksQuery.data,
    meetingsQuery.data,
    tgQuery.data,
    dealsQuery.deals,
    canReviewTelegram,
    weekStart,
    weekEnd,
  ]);

  // The calendar is drawn from tasks + meetings; those two are the CORE
  // sources. The page blanks to a full-page error only when BOTH core sources
  // are gone — a single missing core source still leaves a usable calendar, so
  // it is downgraded to the same inline notice as the secondary sources.
  const isError = tasksQuery.isError && meetingsQuery.isError;
  const error = isError ? (tasksQuery.error ?? meetingsQuery.error) : null;
  const isLoading = tasksQuery.isLoading || meetingsQuery.isLoading;

  const asRetry = (refetchOne: () => Promise<unknown>): (() => Promise<void>) => {
    return () => refetchOne().then(() => undefined);
  };

  const sourceFailures: PlannerSourceFailure[] = [];
  if (tasksQuery.isError) {
    sourceFailures.push({
      key: 'tasks',
      titleKey: 'crm.calendar.source_error.tasks',
      retry: asRetry(tasksQuery.refetch),
    });
  }
  if (overdueTasksQuery.isError) {
    sourceFailures.push({
      key: 'overdue',
      titleKey: 'crm.calendar.source_error.overdue',
      retry: asRetry(overdueTasksQuery.refetch),
    });
  }
  if (meetingsQuery.isError) {
    sourceFailures.push({
      key: 'meetings',
      titleKey: 'crm.calendar.source_error.meetings',
      retry: asRetry(meetingsQuery.refetch),
    });
  }
  if (dealsQuery.isError) {
    sourceFailures.push({
      key: 'deals',
      titleKey: 'crm.calendar.source_error.deals',
      retry: asRetry(dealsQuery.refetch),
    });
  }
  if (canReviewTelegram && tgQuery.isError) {
    sourceFailures.push({
      key: 'telegram',
      titleKey: 'crm.calendar.source_error.telegram',
      retry: asRetry(tgQuery.refetch),
    });
  }

  const refetch = useCallback(async (): Promise<void> => {
    // `refetch()` fires even on queries disabled via `enabled`, so the
    // Telegram query is skipped for roles that cannot review candidates.
    await Promise.all([
      tasksQuery.refetch(),
      overdueTasksQuery.refetch(),
      meetingsQuery.refetch(),
      dealsQuery.refetch(),
      ...(canReviewTelegram ? [tgQuery.refetch()] : []),
    ]);
  }, [tasksQuery, overdueTasksQuery, meetingsQuery, dealsQuery, tgQuery, canReviewTelegram]);

  return {
    events: plannerEvents,
    weekStart,
    weekEnd,
    isLoading,
    isError,
    error: error instanceof Error ? error : null,
    refetch,
    sourceFailures,
    canReviewTelegram,
  };
}
